// ============================================================
// exportCasesCsv.ts
// Hand-rolled (no library) CSV export of the firm's cases — one row
// per case, columns = the case's flat data fields (the same column set
// flattenCase in useAutoSave.ts emits, which mirrors the backend Case
// columns). A machine-readable data-transfer / backup export.
//
// SCOPE (v1): the FLAT case fields only, which already include the
// frozen client snapshot (client* columns). Related records —
// witnesses, deadlines, activities, documents — are NOT included: they
// are one-to-many and don't fit a one-row-per-case shape. A full
// multi-sheet / zipped export is a later enhancement.
//
// The input is RawCase[] — the FLAT backend row shape (fetchCaseById).
// The case-LIST endpoint (getCases) returns a TRIMMED projection, so the
// caller must fetch each case's full row before calling this (see
// CaseListScreen's export handler).
// ============================================================
import { londonToday } from './civilDate'
import type { RawCase } from '../types/case.types'

// Ordered export columns. Mirrors the flattenCase key order (useAutoSave.ts)
// so the CSV column layout matches the app's own field grouping, with two
// identity columns (id, matterNumber) prepended — useful for backup / re-import
// keying. Any column absent from a given RawCase serialises to an empty cell
// (safe — never throws), so the list is resilient to schema drift.
export const CASE_EXPORT_COLUMNS: readonly string[] = [
  // Identity
  'id',
  'matterNumber',
  // User-set case name (was missing — every other whitelisted field exports).
  'caseName',
  // Case control
  'handover', 'openDate', 'closeDate', 'setupFinalised',
  // Client source
  'clientSource', 'clientSourceContactNumber', 'clientSourceEmailAddress',
  'clientSourceAddressLine1', 'clientSourceAddressLine2', 'clientSourceAddressTown',
  'clientSourceAddressCounty', 'clientSourceAddressPostcode', 'clientSourceNotApplicable',
  // Client snapshot — person
  'clientTitle', 'clientForename', 'clientSurname', 'clientAddressLine1', 'clientAddressLine2',
  'clientAddressTown', 'clientAddressCounty', 'clientAddressPostcode', 'clientDateOfBirth',
  'clientHasNiNumber', 'clientNiNumber', 'clientMainContactNumber', 'clientEmailAddress',
  // B3b — "Client has email address" (imports back as the client's flag).
  'clientHasEmailAddress',
  'clientEmploymentStatus', 'clientOccupation',
  // Client snapshot — GP
  'clientGpName', 'clientGpAddressLine1', 'clientGpAddressLine2', 'clientGpAddressTown',
  'clientGpAddressCounty', 'clientGpAddressPostcode', 'clientGpPhone',
  // Client snapshot — hospital
  'clientHospitalName', 'clientHospitalAddressLine1', 'clientHospitalAddressLine2',
  'clientHospitalAddressTown', 'clientHospitalAddressCounty', 'clientHospitalAddressPostcode',
  'clientHospitalPhone',
  // Case-specific client / incident
  'clientTimeOffWork', 'clientDaysOffWork', 'clientDescriptionOfInjuries', 'clientMedicalAttention',
  // Accident
  'askCueReference', 'accidentDate', 'accidentTime', 'vehiclePosition', 'driverName',
  'accidentDescription', 'accidentDescriptionOther', 'accidentLocation', 'accidentLocationMode',
  'accidentLocationFreeText', 'accidentLocationAddressLine1', 'accidentLocationAddressLine2',
  'accidentLocationAddressTown', 'accidentLocationAddressCounty', 'accidentLocationAddressPostcode',
  'wearingSeatbelt', 'claimantReg', 'claimantVehicleResponsible', 'numberOfPeopleInVehicle',
  'policeInvolvement',
  // Injuries
  'injuryNeck', 'injuryShoulder', 'injuryBack', 'injuryRibsChestTorso', 'injuryFaceCheckboneJawNose',
  'injuryForearmWristHandFingers', 'injuryDamageToTeeth', 'injuryHeadSenses', 'injuryHipsPelvisGenitals',
  'injuryLegKneeAnkleFootToe', 'injuryPsychological',
  // B3 (B2c item 4)
  'injuryHearingEars', 'injuryLungsBreathing', 'injurySkin', 'injuryInternalOrgans', 'injuryFatal', 'injuryOther', 'injuryOtherDetails',
  'injuryExceptionallySevere', 'injuryExceptionallySevereDetails',
  // Defendant
  'defendantReg', 'defendantName', 'defendantAddressLine1', 'defendantAddressLine2',
  'defendantAddressTown', 'defendantAddressCounty', 'defendantAddressPostcode',
  'defendantMainContactNumber',
  // TPI
  'tpiInsurer', 'tpiReferenceNumber', 'tpiAddressLine1', 'tpiAddressLine2', 'tpiAddressTown',
  'tpiAddressCounty', 'tpiAddressPostcode',
  // Linked cases (array → joined)
  'linkedCases',
  // Medical agency
  'medicalAgencyName', 'medicalAgencyAddressLine1', 'medicalAgencyAddressLine2',
  'medicalAgencyAddressTown', 'medicalAgencyAddressCounty', 'medicalAgencyAddressPostcode',
  'medicalAgencyPhone', 'medicalAgencyEmail', 'medicalAgencyNotes',
  // Medical expert
  'medicalExpertName', 'medicalExpertAddressLine1', 'medicalExpertAddressLine2',
  'medicalExpertAddressTown', 'medicalExpertAddressCounty', 'medicalExpertAddressPostcode',
  'medicalExpertPhone', 'medicalExpertEmail', 'medicalExpertNotes',
  // TPS
  'tpsName', 'tpsAddressLine1', 'tpsAddressLine2', 'tpsAddressTown', 'tpsAddressCounty',
  'tpsAddressPostcode', 'tpsPhone', 'tpsEmail', 'tpsNotes',
  // Funding
  'deductionMode', 'deductionCustomValue', 'ateProvider',
]

// Serialise ONE value to a plain cell string (pre-escaping):
//   null / undefined      → '' (empty cell)
//   boolean               → 'Yes' / 'No' (human-readable, consistent)
//   array (e.g. linkedCases, clientMedicalAttention) → '; '-joined
//   number / string       → String(v)
const toCell = (v: unknown): string => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (Array.isArray(v)) return neutraliseFormula(v.map((x) => (x === null || x === undefined ? '' : String(x))).join('; '))
  if (typeof v === 'number') return String(v)
  return neutraliseFormula(String(v))
}

// ★ CSV/formula injection: a free-text cell starting with = + - @ (or a
// tab/CR) is executed as a formula when the export is opened in Excel /
// Sheets (e.g. =HYPERLINK(...) exfiltration). Prefix with an apostrophe so
// it's shown as text. Numbers are emitted raw above, so -5 stays numeric.
const neutraliseFormula = (s: string): string => (/^[=+\-@\t\r]/.test(s) ? `'${s}` : s)

// CSV-escape a cell (RFC 4180). This is the critical correctness point of a
// hand-rolled CSV: any value containing a comma, double-quote, CR or LF MUST
// be wrapped in double-quotes, with internal quotes doubled ("" ). Addresses,
// descriptions, and notes WILL contain commas and newlines, so this runs on
// every cell. Values without special chars pass through unquoted.
const escapeCsv = (cell: string): string => {
  if (/[",\r\n]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`
  }
  return cell
}

// Read a column off a RawCase (typed as an interface, so index via a widened
// record). Missing keys → undefined → toCell → '' — never throws.
const readCol = (row: RawCase, col: string): unknown =>
  (row as unknown as Record<string, unknown>)[col]

// Build the full CSV text: header row + one row per case, CRLF-joined
// (Excel-friendly). A trailing CRLF is intentionally omitted.
export const buildCasesCsv = (cases: RawCase[]): string => {
  const header = CASE_EXPORT_COLUMNS.map((c) => escapeCsv(c)).join(',')
  const rows = cases.map((row) =>
    CASE_EXPORT_COLUMNS.map((col) => escapeCsv(toCell(readCol(row, col)))).join(','),
  )
  return [header, ...rows].join('\r\n')
}

// Single-case export filename: casevo-<CASE REF>-<YYYY-MM-DD>.csv (the ref is
// the shared SURNAME-YEAR-ID; unsafe filename chars are replaced). Multi-case
// exports keep the default casevo-cases-<date>.csv.
export const caseCsvFilename = (ref: string): string => {
  const safe = ref.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'case'
  return `casevo-${safe}-${londonToday()}.csv`
}

// Trigger a browser download of the CSV. Local Blob → object URL → temporary
// <a download> → click → revoke. `﻿` BOM prepended so Excel opens the
// UTF-8 file with the correct encoding (accented names etc.).
export const downloadCasesCsv = (cases: RawCase[], filename?: string): void => {
  const csv = buildCasesCsv(cases)
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const name = filename ?? `casevo-cases-${londonToday()}.csv`
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
